/**
 * IndexedDB-Wrapper für Offline-Outbox.
 *
 * Stores:
 * - leads_outbox · queue von Leads die offline erfasst wurden
 *   und beim nächsten Online-Event synct werden
 *
 * Block 9 · UNITI-Phase 1 supportet nur lead-creation offline,
 * keine PATCH/DELETE oder file-uploads außer Visitenkarte.
 */
import { type DBSchema, type IDBPDatabase, openDB } from 'idb';

export interface OutboxLead {
  id: string;
  payload: Record<string, unknown>;
  card_blob?: Blob | null;
  card_mime?: string;
  created_at: number;
  attempts: number;
  last_error?: string;
}

interface LeadCraftDB extends DBSchema {
  leads_outbox: {
    key: string;
    value: OutboxLead;
    indexes: { 'by-created': number };
  };
}

const DB_NAME = 'leadcraft';
const DB_VERSION = 1;

let _dbPromise: Promise<IDBPDatabase<LeadCraftDB>> | null = null;

function getDB(): Promise<IDBPDatabase<LeadCraftDB>> {
  if (!_dbPromise) {
    _dbPromise = openDB<LeadCraftDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('leads_outbox')) {
          const store = db.createObjectStore('leads_outbox', { keyPath: 'id' });
          store.createIndex('by-created', 'created_at');
        }
      },
    });
  }
  return _dbPromise;
}

export async function enqueueLead(
  payload: Record<string, unknown>,
  card?: File | Blob | null,
): Promise<string> {
  const db = await getDB();
  // crypto.randomUUID() (UUIDv4, crypto-strong) verhindert Kollisionen bei rapid-fire
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? `outbox-${crypto.randomUUID()}`
      : `outbox-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const entry: OutboxLead = {
    id,
    payload,
    card_blob: card ?? null,
    card_mime: card?.type,
    created_at: Date.now(),
    attempts: 0,
  };
  try {
    await db.add('leads_outbox', entry);
  } catch (err) {
    // QuotaExceeded → User muss informiert werden, sonst silent data-loss
    throw new Error(`outbox_add_failed:${(err as Error).message}`);
  }
  return id;
}

export async function listOutbox(): Promise<OutboxLead[]> {
  const db = await getDB();
  return (await db.getAllFromIndex('leads_outbox', 'by-created')).reverse();
}

export async function removeFromOutbox(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('leads_outbox', id);
}

export async function markOutboxError(id: string, msg: string): Promise<void> {
  const db = await getDB();
  const entry = await db.get('leads_outbox', id);
  if (!entry) return;
  entry.attempts += 1;
  entry.last_error = msg.slice(0, 200);
  await db.put('leads_outbox', entry);
}

export async function clearOutbox(): Promise<void> {
  const db = await getDB();
  await db.clear('leads_outbox');
}

export async function outboxCount(): Promise<number> {
  const db = await getDB();
  return db.count('leads_outbox');
}

export async function outboxBlockedCount(): Promise<number> {
  const entries = await listOutbox();
  return entries.filter(
    (e) => e.attempts >= 5 || e.last_error?.startsWith('permanent_'),
  ).length;
}
